import type { Qubic, QubicCategory, QubicKind, Subscription } from "api";
import { QUOTE_URL } from "constants/common.constants";

export type StoreCategory = QubicCategory | "all";

/** The `.st-fltr` tabs, in the design's order. "All Products" sends no category. */
export const STORE_CATEGORIES: { key: StoreCategory; label: string }[] = [
  { key: "all", label: "All Products" },
  { key: "data", label: "Logistics Data" },
  { key: "tracking", label: "Tracking" },
  { key: "schedules", label: "Schedules" },
  { key: "rates", label: "Rates & Booking" },
  { key: "intelligence", label: "Intelligence & Analytics" },
  { key: "calculators", label: "Calculators" },
];

export const inCategory = (qubic: Qubic, category: StoreCategory): boolean =>
  category === "all" || qubic.category === category;

/** The cards one kind tab shows, catalogue order kept. */
export const qubicsOfKind = (qubics: Qubic[], kind: QubicKind, category: StoreCategory): Qubic[] =>
  qubics.filter((qubic) => qubic.kinds.includes(kind) && inCategory(qubic, category));

/**
 * The quote form reads `serviceTool` from the query string. A product sold as
 * several tools — or none — has no single one to name, so it opens the form bare.
 */
export const requestAccessUrl = (qubic: Qubic): string =>
  qubic.service_tools.length === 1
    ? `${QUOTE_URL}?serviceTool=${encodeURIComponent(qubic.service_tools[0])}`
    : QUOTE_URL;

/** One `.st-use__i`: a count, and a bar only when there is a limit to fill. */
export type UsageAxis = {
  used: number;
  /** Null when any tool behind the figure has no cap — then no bar is drawn. */
  limit: number | null;
  /** 0–100, null exactly when `limit` is. */
  percent: number | null;
};

export type OwnedUsage = {
  calls: UsageAxis;
  /** Null when the product is not capped on unique shipments at all. */
  unique: UsageAxis | null;
  /** A tool behind the card is being refused by the gateway right now. */
  out: boolean;
};

const sum = (values: number[]): number => values.reduce((total, value) => total + value, 0);

const percentOf = (used: number, limit: number): number =>
  limit <= 0 ? 100 : Math.min(100, Math.round((used / limit) * 100));

/**
 * One axis over several rows. A single row keeps the back office's own percent;
 * a sum is only given a limit when every row has one, because a missing cap
 * would make the total a fraction of nothing in particular.
 */
const axisOf = (used: number[], limits: (number | null)[], percents: (number | null)[]): UsageAxis => {
  const total = sum(used);

  if (limits.some((limit) => limit === null)) {
    return { used: total, limit: null, percent: null };
  }

  const limit = sum(limits as number[]);
  const percent = used.length === 1 && percents[0] !== null ? percents[0] : percentOf(total, limit);

  return { used: total, limit, percent };
};

/**
 * The usage an owned Store card shows: the subscription rows of the tools the
 * company holds under this product. Global Tracking is two tools, so its bars
 * are the container and air rows added together. Null when there is no row to
 * read — the card then shows no figures rather than a zero.
 */
export const ownedUsage = (qubic: Qubic, subscriptions: Subscription[]): OwnedUsage | null => {
  const rows = subscriptions.filter((row) => qubic.owned_tools.includes(row.service_tool));

  if (rows.length === 0) return null;

  const calls = axisOf(
    rows.map((row) => row.usage.used),
    rows.map((row) => row.monthly_limit),
    rows.map((row) => row.usage.percent)
  );

  // Only the tracking tools count unique shipments; a row without the axis
  // would leave the sum short, so the axis is dropped rather than understated.
  const hasUnique = rows.every((row) => row.usage.unique_used !== null);
  const unique = hasUnique
    ? axisOf(
        rows.map((row) => row.usage.unique_used as number),
        rows.map((row) => row.monthly_unique_limit),
        rows.map((row) => row.usage.unique_percent)
      )
    : null;

  return { calls, unique, out: rows.some((row) => row.limit.reached_on.length > 0) };
};

/**
 * The Web tab's widgets with the catalogue product each one embeds. A widget
 * whose product is missing from the catalogue, or not sold for web, is left out.
 */
export const webWidgets = <W extends { slug: string }>(
  widgets: W[],
  qubics: Qubic[],
  category: StoreCategory
): { widget: W; qubic: Qubic }[] => {
  const bySlug = new Map(qubics.map((qubic) => [qubic.slug, qubic]));

  return widgets.flatMap((widget) => {
    const qubic = bySlug.get(widget.slug);

    return qubic !== undefined && qubic.kinds.includes("web") && inCategory(qubic, category) ? [{ widget, qubic }] : [];
  });
};
